<script setup lang="ts">
//#region import
import { computed, nextTick, onBeforeMount, onMounted, ref, watch } from 'vue';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useTypedStore } from '@/store';
import { useSessionAction } from '@/composables/useSessionAction';
import { useApi } from '@/api';
import type { GetControlTowerFeesBySiteIdResponse } from '@/types/openapi-ts';
//#endregion

//#region types
type FeesKeys = keyof GetControlTowerFeesBySiteIdResponse['payload'];
export type FeesIndex = GetControlTowerFeesBySiteIdResponse['payload'] & Record<FeesKeys, {
  field?: string
}>;

//#endregion

//#region const
const sortingArr = ['deposit', 'withdrawal', 'sport', 'casino', 'gaming_tax', 'admin'];
//#endregion

//#region composables
const store = useTypedStore();
const api = useApi();
const { onSessionUnlock } = useSessionAction();
const popupManager = usePopupManager();
//#endregion

//#region state
const isLoading = ref(true);
const lazyLoading = ref(true);
const currentBrand = ref('');
const fees = ref<FeesIndex>({
  admin: {
    default_fee_percent: 0,
    custom_fees: [],
  },
  casino: {
    default_fee_percent: 0,
    custom_fees: [],
  },
  deposit: {
    default_fee_percent: 0,
    custom_fees: [],
  },
  gaming_tax: {
    default_fee_percent: 0,
    custom_fees: [],
  },
  sport: {
    default_fee_percent: 0,
    custom_fees: [],
  },
  withdrawal: {
    default_fee_percent: 0,
    custom_fees: [],
  },
});
//#endregion

//#region computed
const brands = computed(() => store.getters['misc/brands']);
const countriesCode = computed(() => {
  return countries.value.reduce((acc, item) => {
    acc[item.code] = item;
    return acc;
  }, {} as Record<string, typeof countries.value[number]>);
});
const countries = computed(() => {
  return store.state.misc.countries || [];
});
//#endregion

//#region methods
async function fetchData(id: string) {
  isLoading.value = true;
  try {
    const res = await api.getFees(id);
    fees.value = Object.entries(res.data.payload)
      .sort((a, b) => sortingArr.indexOf(a[0]) - sortingArr.indexOf(b[0]))
      .reduce((acc, [key, value_1]) => {
        acc[key as FeesKeys] = value_1;
        acc[key as FeesKeys].field = key;
        return acc;
      }, {} as FeesIndex);
  } finally {
    isLoading.value = false;
  }
}

function onSelectBrand(id: string) {
  currentBrand.value = id;
}

function handleEditFeesPopup() {
  popupManager.editFees.open({
    data: {
      brandId: currentBrand.value,
      fees: fees.value,
    },
    listeners: {
      success: (_fees) => {
        fees.value = _fees;
      },
    },
  });
};
//#endregion

//#region watch
watch(currentBrand, (id) => {
  fetchData(id!);
});
//#endregion

//#region hooks
onMounted(() =>{
  nextTick(() => {
    lazyLoading.value = false;
  });
});

onBeforeMount(() => {
  onSessionUnlock.value = () => {
    if (brands.value[0]!.id === currentBrand.value) {
      fetchData(brands.value[0]!.id);
    } else {
      onSelectBrand(brands.value[0]!.id);
    }
  };

  onSelectBrand(brands.value[0]!.id);
});
//#endregion
</script>

<template>
  <div class="fees">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.fees.fees') }}
      </template>
      <template v-slot:actions>
        <UiButton
          icon="pen"
          filled
          @click="handleEditFeesPopup"
        >
          {{ $t('settings.fees.edit_fees') }}
        </UiButton>
      </template>
    </UiToolbar>
    <BrandsList
      :current-brand="currentBrand"
      @brand-change="onSelectBrand"
    />
    <div
      v-loading="isLoading"
      :class="[
        't:grid t:grid-cols-6 t:gap-16',
        { 'fees__lazy-loading': lazyLoading }
      ]"
    >
      <div
        v-for="block in fees"
        :key="block.field"
        class="fees__list-item"
      >
        <CardTitle
          :title="block.field ? $t(`settings.fees.${block.field}`) : ''"
        />
        <div class="t:px-8">
          <CardItem
            :item="{name: $t('settings.fees.default'), value: `${block.default_fee_percent}%`}"
            no-border
          />
          <CardItem
            v-for="item in block.custom_fees"
            :key="item.country_code"
            :item="{name: countriesCode[item.country_code]!.name, value: `${item.fee_percent}%`}"
            is-label-ellipsis
            :is-content-ellipsis="false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.fees {
  &__brands {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 16px;
    border-bottom: 1px solid var(--border-divider);
  }
  &__brands-item {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    color: var(--text-body);
    font-size: 16px;
    height: 36px;
    position: relative;

    &_active {
      font-weight: 500;
      &:after {
        content: '';
        left: 0;
        right: 0;
        height: 2px;
        background-color: var(--border-focused);
        position: absolute;
        bottom: 0;
      }
    }
  }
  &__brands-item-img {
    width: 16px;
    height: 16px;
    margin-right: 8px;
    border-radius: 4px;
  }

  &__lazy-loading {
    display: none;
  }
}
</style>
